// .fluveria file boundary. The format (engine FORMAT.md) is owned by the
// engine's fluveria-document crate; this module only maps the engine's
// document snapshot onto AppStateSnapshot. v2 json-tree only — legacy v1
// support was removed after the one-time conversion.

import {
  parseDocumentBytesWasm,
  parseDocumentWasm,
  serializeDocumentBinaryWasm,
  serializeDocumentWasm,
  validateDocumentSnapshotWasm,
} from 'fluveria-engine-wasm';
import type { AppState } from '../state/appState.js';
import { createDocumentEntityId } from '../ids/entityIds.js';
import { ROOT_DATA_RECORDS, type DataFieldDef, type DataFieldType, type DataSourceDef } from '../graph/dataGraph.js';
import {
  createFileDocumentSnapshot,
  createFileWorkspaceSnapshot,
  restoreStateSnapshot,
  type AppStateSnapshot,
} from '../state/snapshot.js';

export const FLUVERIA_FILE_EXTENSION = 'fluveria';

interface EngineDocumentSnapshot {
  id?: string;
  params: AppStateSnapshot['params'];
  scene: AppStateSnapshot['scene'];
  components: AppStateSnapshot['componentLibrary'];
  layers: AppStateSnapshot['layerLibrary'];
  favorites: AppStateSnapshot['sceneFavorites'];
  geometry: AppStateSnapshot['geometryLibrary'];
  materials: AppStateSnapshot['materialLibrary'];
  shapes?: AppStateSnapshot['shapes'];
  dataGraphs: AppStateSnapshot['dataGraphs'];
  queryGraphs?: AppStateSnapshot['queryGraphs'];
  dataSources?: AppStateSnapshot['dataSources'];
  data?: NonNullable<AppStateSnapshot['data']>;
  runtimeState?: AppStateSnapshot['runtimeState'];
  eventGraphs?: AppStateSnapshot['eventGraphs'];
  lights?: AppStateSnapshot['lights'];
  types?: AppStateSnapshot['types'];
  workspace?: {
    slot?: AppStateSnapshot['editorGraphSlot'];
    mode?: AppStateSnapshot['editorGraphMode'];
    graphKind?: AppStateSnapshot['editorGraphKind'];
    dataGraphId?: string;
  };
}

/**
 * The document as the engine's codec takes it -- what is saved, and what the
 * running document's behaviours are ticked over (dataRuntime).
 */
export function toEngineSnapshot(appState: AppState): EngineDocumentSnapshot {
  const document = createFileDocumentSnapshot(appState);
  const workspace = createFileWorkspaceSnapshot(appState);
  return {
    id: document.documentId,
    params: document.params,
    scene: document.scene,
    components: document.componentLibrary,
    layers: document.layerLibrary,
    favorites: document.sceneFavorites,
    geometry: document.geometryLibrary,
    materials: document.materialLibrary,
    shapes: document.shapes,
    dataGraphs: document.dataGraphs,
    queryGraphs: document.queryGraphs,
    dataSources: document.dataSources,
    runtimeState: document.runtimeState,
    eventGraphs: document.eventGraphs,
    ...(document.lights.length > 0 ? { lights: document.lights } : {}),
    types: document.types,
    ...(document.data ? { data: document.data } : {}),
    workspace: {
      slot: workspace.editorGraphSlot,
      mode: workspace.editorGraphMode,
      graphKind: workspace.editorGraphKind,
      ...(workspace.activeDataGraphId ? { dataGraphId: workspace.activeDataGraphId } : {}),
    },
  };
}

function fromEngineSnapshot(snapshot: EngineDocumentSnapshot): AppStateSnapshot {
  return {
    // The format makes the id optional, and the native editor writes none
    // (every showcase of the engine is without one); such a document is
    // given one here, as a template is, and keeps it from its next save.
    documentId: snapshot.id || createDocumentEntityId(),
    params: snapshot.params ?? {},
    scene: snapshot.scene,
    componentLibrary: snapshot.components ?? {},
    layerLibrary: snapshot.layers ?? {},
    sceneFavorites: snapshot.favorites ?? [],
    geometryLibrary: snapshot.geometry ?? {},
    materialLibrary: snapshot.materials ?? {},
    shapes: snapshot.shapes ?? {},
    dataGraphs: snapshot.dataGraphs ?? {},
    queryGraphs: snapshot.queryGraphs ?? {},
    dataSources: Object.fromEntries(Object.entries(snapshot.dataSources ?? {}).map(([id, source]) => [
      id,
      normalizeDataSource(source),
    ])),
    data: snapshot.data ?? null,
    runtimeState: snapshot.runtimeState ?? {},
    eventGraphs: snapshot.eventGraphs ?? {},
    lights: snapshot.lights ?? [],
    types: snapshot.types ?? {},
    editorGraphSlot: snapshot.workspace?.slot as AppStateSnapshot['editorGraphSlot'],
    editorGraphMode: snapshot.workspace?.mode as AppStateSnapshot['editorGraphMode'],
    editorGraphKind: snapshot.workspace?.graphKind as AppStateSnapshot['editorGraphKind'],
    activeDataGraphId: snapshot.workspace?.dataGraphId ?? null,
    activeEventGraphId: null,
    activeQueryGraphId: null,
  };
}

function normalizeDataField(fieldId: string, value: unknown): DataFieldDef {
  if (typeof value === 'string') return { path: [fieldId], type: value as DataFieldType };
  const field = value as Partial<DataFieldDef> | null;
  return {
    path: Array.isArray(field?.path) && field.path.every(segment => typeof segment === 'string')
      ? [...field.path]
      : [fieldId],
    type: (field?.type ?? 'string') as DataFieldType,
  };
}

function normalizeDataSource(source: unknown): DataSourceDef {
  const value = source as Partial<DataSourceDef> & { path?: string };
  return {
    provider: value.provider ?? 'json',
    resource: value.resource ?? value.path ?? '',
    ...(value.credential ? { credential: value.credential } : {}),
    records: value.records ?? structuredClone(ROOT_DATA_RECORDS),
    keyType: value.keyType ?? 'string',
    fields: Object.fromEntries(Object.entries(value.fields ?? {}).map(([fieldId, definition]) => [
      fieldId,
      normalizeDataField(fieldId, definition),
    ])),
  };
}

interface ValidationReport {
  errors: Array<{ path: string; message: string }>;
  warnings: Array<{ path: string; message: string }>;
}

function assertValidSnapshot(snapshot: EngineDocumentSnapshot): void {
  const report = validateDocumentSnapshotWasm(snapshot) as ValidationReport;
  if (report.errors.length === 0) return;
  const first = report.errors[0];
  throw new Error(`${first.path || 'document'}: ${first.message}`);
}

/** Canonical on-disk form: the binary container. */
export function serializeFileBinary(appState: AppState): Uint8Array {
  return serializeDocumentBinaryWasm(toEngineSnapshot(appState));
}

/** Text projection (v2 json-tree) — debugging and agents. */
export function serializeFile(appState: AppState): string {
  return serializeDocumentWasm(toEngineSnapshot(appState));
}

/** Binary container or v2 text, auto-detected by the engine. */
export function parseFileBytes(data: Uint8Array): AppStateSnapshot {
  const snapshot = parseDocumentBytesWasm(data) as EngineDocumentSnapshot;
  assertValidSnapshot(snapshot);
  return fromEngineSnapshot(snapshot);
}

export function parseFileText(data: string): AppStateSnapshot {
  const snapshot = parseDocumentWasm(data) as EngineDocumentSnapshot;
  assertValidSnapshot(snapshot);
  return fromEngineSnapshot(snapshot);
}

export function deserializeFile(appState: AppState, data: string | Uint8Array): void {
  const snapshot = typeof data === 'string' ? parseFileText(data) : parseFileBytes(data);
  restoreStateSnapshot(appState, snapshot);
}

/** Object-form convenience for callers holding already-parsed JSON. */
export function restoreFile(appState: AppState, value: unknown): void {
  deserializeFile(appState, JSON.stringify(value));
}
